/** packages/shared-components/src/hooks/useMessage.tsx */

import { useCallback } from "react";
import { createRoot } from "react-dom/client";
import { getSubAppPortalContainer } from "@eweadn-qiankun/shared-utils";
import { Message, type MessageType } from "../components/Message";

export interface MessageOptions {
  message: string;
  type?: MessageType;
  duration?: number;
}

/** 跨跳转/整页刷新仍要弹出的 Message（如固件升级成功） */
const PENDING_MESSAGE_KEY = "eweadn:pendingMessage";
const PENDING_MESSAGE_TTL_MS = 60_000;

/** 相同 type+文案在窗口内只弹一次，避免改键等场景连点叠一堆 */
const MESSAGE_DEBOUNCE_MS = 500;
let lastMessageKey = "";
let lastMessageAt = 0;

type PendingMessagePayload = MessageOptions & { expiresAt: number };

/** 写入待弹出 Message，供跳转/刷新后 flush */
export function queuePendingMessage(options: MessageOptions) {
  if (typeof sessionStorage === "undefined") return;
  try {
    const payload: PendingMessagePayload = {
      message: options.message,
      type: options.type || "info",
      duration: options.duration || 3000,
      expiresAt: Date.now() + PENDING_MESSAGE_TTL_MS,
    };
    sessionStorage.setItem(PENDING_MESSAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

/** 消费并弹出跨跳转待显示 Message（主应用挂载/回首页时调用） */
export function flushPendingMessage() {
  if (typeof sessionStorage === "undefined") return;
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_MESSAGE_KEY);
    if (!raw) return;
    sessionStorage.removeItem(PENDING_MESSAGE_KEY);
  } catch {
    return;
  }

  try {
    const payload = JSON.parse(raw) as PendingMessagePayload;
    if (
      !payload?.message ||
      typeof payload.expiresAt !== "number" ||
      Date.now() > payload.expiresAt
    ) {
      return;
    }
    // 跳转后允许再次弹出同一文案
    lastMessageKey = "";
    lastMessageAt = 0;
    showMessage({
      message: payload.message,
      type: payload.type || "info",
      duration: payload.duration || 3000,
    });
  } catch {
    // ignore malformed payload
  }
}

/**
 * 立刻弹出，并写入 pending，避免 location 跳转/子应用卸载把 Message 弄丢。
 * 不依赖页面是否真的跳转成功。
 */
export function showMessageAcrossNavigation(options: MessageOptions) {
  queuePendingMessage(options);
  showMessage(options);
}

/** 命令式弹出 Message，可在非 React 环境（如 axios 拦截器）中使用 */
export function showMessage(options: MessageOptions) {
  const type = options.type || "info";
  const key = `${type}:${options.message}`;
  const now = Date.now();
  if (key === lastMessageKey && now - lastMessageAt < MESSAGE_DEBOUNCE_MS) {
    return;
  }
  lastMessageKey = key;
  lastMessageAt = now;

  const container = document.createElement("div");
  container.className = "shared-message-container";
  container.style.flex = "none";
  const portalParent = getSubAppPortalContainer();
  portalParent.appendChild(container);

  const root = createRoot(container);

  const handleClose = () => {
    root.unmount();
    if (portalParent.contains(container)) {
      portalParent.removeChild(container);
    }
  };

  root.render(
    <Message
      message={options.message}
      type={type}
      duration={options.duration || 3000}
      onClose={handleClose}
    />
  );
}

export const useMessage = () => {
  const showMessageCallback = useCallback((options: MessageOptions) => {
    showMessage(options);
  }, []);

  const success = useCallback(
    (message: string, duration?: number) => {
      showMessageCallback({ message, type: "success", duration });
    },
    [showMessageCallback]
  );

  const error = useCallback(
    (message: string, duration?: number) => {
      showMessageCallback({ message, type: "error", duration });
    },
    [showMessageCallback]
  );

  const warning = useCallback(
    (message: string, duration?: number) => {
      showMessageCallback({ message, type: "warning", duration });
    },
    [showMessageCallback]
  );

  const info = useCallback(
    (message: string, duration?: number) => {
      showMessageCallback({ message, type: "info", duration });
    },
    [showMessageCallback]
  );

  /** 固件升级等：重启结束立刻提示，跳转首页也不会丢 */
  const successAcrossNavigation = useCallback(
    (message: string, duration?: number) => {
      showMessageAcrossNavigation({
        message,
        type: "success",
        duration: duration ?? 4000,
      });
    },
    []
  );

  return {
    showMessage: showMessageCallback,
    success,
    successAcrossNavigation,
    error,
    warning,
    info,
  };
};
